import { PERMISSIONS } from 'api/User/types'
import classNames from 'classnames'
import CommonIcon from 'CommonIcon'
import { InfoElement } from 'components'
import { InfoArrow } from 'components/InfoArrow'
import { CurrencyValue } from 'CurrencyValue'
import type { DetailsData } from 'data/types'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import RestrictedView from 'RestrictedView'
import type { State } from 'store/types'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'

import useStyles from './useStyles'
import { getCurrencyValue, getPercent } from './utils'

interface Props {
  data: DetailsData
}

function DetailsECom({ data }: Props) {
  const { t: translate } = useTranslation()
  const classes = useStyles()
  const isProd = useSelector((state: State) => state.system.isProd)

  return (
    <Grid container item xs={9}>
      <Grid container item xs={12} className={classes.row1} mt="0.7rem">
        <Grid container item xs={5} alignItems="baseline" display="row">
          <CurrencyValue baseFontSize={1.75} currency={data.currency} value={data.revenueCurrentECom} />
          <InfoElement className={classes.percent} percent={data.revenuePercentECom} />
        </Grid>
        <Grid container item xs={7} alignItems="baseline" display="row">
          <Box className={`fm fm-order-checked ${classes.label} ${classes.orderChecked}`} alignSelf="baseline" />
          <CurrencyValue baseFontSize={1.75} currency={data.currency} value={data.ordersValueCurrent} />
          <InfoElement className={classes.percent} percent={data.ordersValuePercent} />
        </Grid>
      </Grid>

      <Grid container item xs={5} alignItems="baseline" display="row" mt="0.25rem">
        <RestrictedView permission={PERMISSIONS.MARGIN}>
          <i className={`fm fm-margin ${classes.label}`} />
          <div className={classes.marginPercent}>{getPercent(data.marginCurrentECom, translate)}</div>
          <div className={classes.marginValue}>{getCurrencyValue(data.marginValueCurrentECom, data.currency, translate)}</div>
          <InfoArrow current={data.marginValueCurrentECom} compared={data.marginValueComparedECom} />
        </RestrictedView>
      </Grid>

      {isProd && <Grid container item xs={7} alignItems="baseline" display="row" mt="0.25rem" />}
      {!isProd && (
        <Grid container item xs={7} alignItems="baseline" display="row" mt="0.25rem">
          <i className={classNames('fm fm-traffic-web', classes.label)} />
          {getCurrencyValue(data.trafficCurrentECom, '', translate)}
          <InfoElement className={classes.percent} percent={data.trafficPercentECom} />

          <Box className={classNames('fm fm-conversion', classes.label)} ml="0.5rem" />
          <div className={classes.ecom}>{getPercent(data.conversionCurrentECom, translate)}</div>
          <InfoElement className={classes.percent} percent={data.conversionComparedECom} suffix={translate('revenue.pp')} />
        </Grid>
      )}

      <Grid container item xs={5} display="row">
        <RestrictedView permission={PERMISSIONS.MARGIN}>
          <CommonIcon className={classNames(classes.label, classes.lastYear)} name="lastYear" />
          <div className={classes.marginPercent}>{getPercent(data.marginComparedECom, translate)}</div>
          <div className={classes.marginComparedValue}>
            {getCurrencyValue(data.marginValueComparedECom, data.currency, translate)}
          </div>
        </RestrictedView>
      </Grid>
      <Grid container item xs={7} alignItems="baseline" display="row">
        <i className={classNames('fm fm-returns', classes.label)} />
        {getPercent(data.eReturnsValueShareCurrent, translate)}
        <RestrictedView permission={PERMISSIONS.MARGIN}>
          {' | '}
          <i className={classNames('fm fm-returns-necom', classes.label)} />
          <div className={classes.marginPercent}>{getPercent(data.eReturnsValueShareCurrentNECom, translate)}</div>
          <i className={classNames('fm fm-returns-ecom', classes.label)} />
          <div className={classes.marginPercent}>{getPercent(data.eReturnsValueShareCurrentECom, translate)}</div>
        </RestrictedView>
      </Grid>

      <Grid container item xs={12} className={classes.row3} alignItems="baseline">
        <Grid container item xs={5}>
          <i className={classNames('fm fm-oms', classes.label)} />
          <div className={classes.marginPercent}>{getPercent(data.ordersValueOMSShareCurrent, translate)}</div>
          <Box className={classes.marginComparedValue}>
            {getCurrencyValue(data.ordersValueOMSCurrent, data.currency, translate)}
          </Box>
        </Grid>
        <Grid container item xs={6} alignItems="baseline">
          <i className={`fm fm-instore ${classes.label}`} />
          {getPercent(data.ordersValueInStoreShareCurrent, translate)}
          {' | '}
          {getCurrencyValue(data.ordersValueInStoreCurrent, data.currency, translate)}
        </Grid>
      </Grid>
    </Grid>
  )
}

export default DetailsECom
